import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, 
  Button, Container, Chip, CircularProgress, Grid, TableContainer, Divider 
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import InventoryIcon from "@mui/icons-material/Inventory";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { alpha } from "@mui/material/styles";
import { getPurchaseInvoiceById } from "../services/purchaseService";
import { toast } from 'react-toastify';

function PurchaseOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPurchaseInvoice();
  }, [id]);

  const loadPurchaseInvoice = async () => {
    try {
      setLoading(true);
      const data = await getPurchaseInvoiceById(id);
      setInvoice(data.payload || data);
    } catch (error) {
      console.error('Error loading purchase invoice:', error);
      toast.error('Failed to load purchase invoice');
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ pt: 8 }}>
        <Paper elevation={2} sx={{ p: 4, borderRadius: 3, textAlign: "center" }}>
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>Loading Purchase Invoice...</Typography>
        </Paper>
      </Container>
    );
  }

  if (!invoice) {
    return (
      <Container maxWidth="lg" sx={{ pt: 8 }}>
        <Paper elevation={3} sx={{ p: 6, borderRadius: 3, textAlign: "center", bgcolor: '#fff5f5' }}>
          <Typography variant="h5" color="error" mb={2} fontWeight="bold">
            ⚠️ Purchase Invoice Not Found
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            The requested purchase invoice could not be found in the system.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate("/purchases")}
            sx={{ borderRadius: 2, px: 4 }}
          >
            Back to Purchase Orders
          </Button>
        </Paper>
      </Container>
    );
  }

  const subTotal = invoice.SubTotal || 0;
  const totalTax = invoice.TaxAmount || 0;
  const totalAmount = invoice.TotalAmount || 0;
  const discountAmount = invoice.DiscountAmount || 0;

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pt: 2, pb: 4 }}>
      <Container maxWidth="lg">
        {/* Compact Header */}
        <Box sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 2,
          p: 2.5,
          mb: 3,
          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)'
        }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Button 
                variant="contained" 
                size="small"
                startIcon={<ArrowBackIcon />} 
                onClick={() => navigate("/purchases")}
                sx={{ 
                  bgcolor: 'white', 
                  color: '#667eea',
                  '&:hover': { bgcolor: '#f3f4f6' },
                  borderRadius: 1.5,
                  px: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Back
              </Button>
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', p: 1, borderRadius: 1.5 }}>
                <ReceiptIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="bold" color="white">
                  {invoice.PurchaseInvoiceNumber || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                  Ref: {invoice.ReferenceNumber || 'N/A'}
                </Typography>
              </Box>
            </Box>
            <Chip 
              label={invoice.Status || 'Unknown'} 
              sx={{ 
                bgcolor: invoice.Status === 'Paid' ? '#10b981' : '#f59e0b',
                color: 'white',
                fontWeight: 600,
                fontSize: '0.875rem'
              }} 
            />
          </Box>
        </Box>

        <Grid container spacing={2} mb={3}>
          {/* Compact Summary Cards */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: '#667eea', color: 'white' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>Total Amount</Typography>
                  <Typography variant="h5" fontWeight="bold">€{totalAmount.toFixed(2)}</Typography>
                </Box>
                <AttachMoneyIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: '#f59e0b', color: 'white' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>Sub Total</Typography>
                  <Typography variant="h5" fontWeight="bold">€{subTotal.toFixed(2)}</Typography>
                </Box>
                <ShoppingCartIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: '#06b6d4', color: 'white' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>Tax Amount</Typography>
                  <Typography variant="h5" fontWeight="bold">€{totalTax.toFixed(2)}</Typography>
                </Box>
                <ReceiptIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: '#10b981', color: 'white' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>Items</Typography>
                  <Typography variant="h5" fontWeight="bold">{invoice.LineItems?.length || 0}</Typography>
                </Box>
                <InventoryIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          {/* Supplier & Invoice Info */}
          <Grid item xs={12} md={5}>
            <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" mb={2} color="primary">
                📋 Invoice Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: '#f9fafb', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">Supplier</Typography>
                  <Typography variant="body2" fontWeight="600">{invoice.Supplier?.Name || 'N/A'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: '#f9fafb', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography variant="body2" fontWeight="600">{invoice.Supplier?.PhoneNumber || 'N/A'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: '#f9fafb', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">BTW Number</Typography>
                  <Typography variant="body2" fontWeight="600">{invoice.Supplier?.BTWNumber || 'N/A'}</Typography>
                </Box>
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: '#f9fafb', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">Invoice Date</Typography>
                  <Typography variant="body2" fontWeight="600">
                    {invoice.InvoiceDate ? new Date(invoice.InvoiceDate).toLocaleDateString('en-GB') : 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: '#f9fafb', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">Due Date</Typography>
                  <Typography variant="body2" fontWeight="600">
                    {invoice.DueDate ? new Date(invoice.DueDate).toLocaleDateString('en-GB') : 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Line Items Table */}
          <Grid item xs={12} md={7}>
            <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" mb={2} color="primary">
                🛒 Line Items
              </Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.8rem' }}>#</TableCell>
                      <TableCell sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.8rem' }}>Product</TableCell>
                      <TableCell sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.8rem' }}>SKU</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.8rem' }}>Qty</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.8rem' }}>Unit Cost</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.8rem' }}>Tax %</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.8rem' }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoice.LineItems && invoice.LineItems.length > 0 ? (
                      invoice.LineItems.map((item, idx) => (
                        <TableRow key={item.Id || idx} hover>
                          <TableCell sx={{ fontSize: '0.85rem' }}>{idx + 1}</TableCell>
                          <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{item.ProductName || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{item.ProductSKU || 'N/A'}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.85rem' }}>{item.Quantity || 0}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.85rem' }}>€{(item.UnitCost || 0).toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.85rem' }}>{item.TaxPercentage || 0}%</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#059669' }}>
                            €{(item.LineTotal || 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary" fontSize="0.9rem">No items found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {/* Summary Row */}
                    {invoice.LineItems && invoice.LineItems.length > 0 && (
                      <>
                        <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                          <TableCell colSpan={6} align="right" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                            Subtotal:
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                            €{subTotal.toFixed(2)}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                          <TableCell colSpan={6} align="right" sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#ec4899' }}>
                            Tax:
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#ec4899' }}>
                            €{totalTax.toFixed(2)}
                          </TableCell>
                        </TableRow>
                        {discountAmount > 0 && (
                          <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                            <TableCell colSpan={6} align="right" sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#f59e0b' }}>
                              Discount:
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#f59e0b' }}>
                              -€{discountAmount.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow sx={{ bgcolor: '#667eea' }}>
                          <TableCell colSpan={6} align="right" sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'white' }}>
                            Grand Total:
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: 'white' }}>
                            €{totalAmount.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default PurchaseOrderDetailPage;